import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, Text } from '../../../../../components';
import { CASINO_TECH_STATE } from '../../../../../theme/tokens';
import { AuroraField } from '../AuroraField';
import { Root, Scrim, Content, Hero, Glyph, Headline, Actions, VisitButton, DetailsLink } from './ConfirmationCard.styles';

/** Bare host ("brand.casino") → absolute https URL for the Visit link. */
const toHref = (domain: string): string => (/^https?:\/\//i.test(domain) ? domain : `https://${domain}`);

/** Plain right arrow (currentColor) — nudges on hover via the `.visit-arrow` class. */
const ArrowRight = (): React.ReactElement => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M3 8h9M8.5 4.5L12 8l-3.5 3.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export type ConfirmationCardProps = {
  /** Active brand name for the headline ("{brand} is live"); falls back when blank. */
  brandName?: string;
  /** The active brand's domain; the Visit CTA is disabled while it is blank. */
  domain?: string;
  /** Opens the deployment summary modal (the "View details" link). */
  onOpenSummary: () => void;
  /** Drives the card's own aurora — static under reduced motion, animated otherwise. */
  reduced: boolean;
};

/**
 * The terminal success card — enters as the final bounce slot when the deploy finishes.
 * Shares the fan-positioning shell so it sits in the stack identically, over a near-black
 * base with its OWN aurora (the live motion the section's aurora hands off once done) and
 * a magenta -> cyan gradient rim. The success check pops in (after the card lands), the
 * headline names the brand, the primary CTA opens the live site (disabled until a domain
 * exists), and a plain-text "View details" link under it opens the full step summary.
 */
export const ConfirmationCard = React.forwardRef<HTMLElement, ConfirmationCardProps>(
  ({ brandName, domain, onOpenSummary, reduced }, ref) => {
    const { t } = useTranslation();
    const trimmed = domain?.trim() ?? '';
    const hasDomain = trimmed.length > 0;
    const name = brandName?.trim() ?? '';

    const openSite = React.useCallback(() => {
      if (!hasDomain) return;
      window.open(toHref(trimmed), '_blank', 'noopener,noreferrer');
    }, [hasDomain, trimmed]);

    return (
      <Root ref={ref}>
        <AuroraField reduced={reduced} />
        <Scrim aria-hidden="true" />
        <Content>
          <Hero>
            <Glyph>
              <Icon name="check" size={22} color={CASINO_TECH_STATE.live.accent} />
            </Glyph>
            {name ? (
              <Headline text="{{name}} is live" placeholders={{ name }} tag="span" />
            ) : (
              <Headline text="Your website is live" tag="span" />
            )}
          </Hero>
          <Actions>
            <VisitButton type="button" onClick={openSite} disabled={!hasDomain}>
              <Text text="Visit now" tag="span" />
              <span className="visit-arrow">
                <ArrowRight />
              </span>
            </VisitButton>
            <DetailsLink type="button" onClick={onOpenSummary}>
              {t('View details')}
            </DetailsLink>
          </Actions>
        </Content>
      </Root>
    );
  },
);

ConfirmationCard.displayName = 'ConfirmationCard';

export default ConfirmationCard;
